import React from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send, Utensils, ShoppingBag, Truck } from "lucide-react";
import { RESTAURANT, HOURS } from "@/data/menu";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = React.useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has been received.");
      setForm({ name: "", email: "", phone: "", message: "" });
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-[#FBF8F3]">
      {/* HEADER */}
      <section className="px-6 md:px-12 lg:px-24 pt-36 pb-12 max-w-[1500px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="eyebrow mb-5">Contact</div>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl text-[#2A1B3D] leading-[0.95] tracking-tight">
            Come <span className="italic">say hello</span>.
          </h1>
          <p className="mt-6 text-lg text-[#4A3F55] max-w-2xl">
            Reservations, takeout, private events, or just to tell us your favorite dish —
            we&apos;d love to hear from you.
          </p>
        </motion.div>
      </section>

      {/* MAIN GRID */}
      <section className="px-6 md:px-12 lg:px-24 pb-20 max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-12">
        {/* INFO COLUMN */}
        <div className="lg:col-span-5 space-y-10" data-testid="contact-info">
          <div>
            <div className="eyebrow mb-4">Find Us</div>
            <p className="text-[#2A1B3D] text-lg flex items-start gap-3 leading-relaxed">
              <MapPin className="w-5 h-5 text-[#E91E63] mt-1 shrink-0" />
              <span>{RESTAURANT.address}</span>
            </p>
            <a
              href={RESTAURANT.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions"
              className="inline-block mt-3 text-sm tracking-[0.22em] uppercase font-bold text-[#E91E63] hover:underline"
            >
              Get Directions →
            </a>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="eyebrow mb-3">Call</div>
              <a href={RESTAURANT.phoneHref} data-testid="contact-phone" className="text-[#2A1B3D] flex items-center gap-2 hover:text-[#E91E63]">
                <Phone className="w-4 h-4" /> {RESTAURANT.phone}
              </a>
            </div>
            <div>
              <div className="eyebrow mb-3">Email</div>
              <a href={RESTAURANT.emailHref} data-testid="contact-email" className="text-[#2A1B3D] flex items-center gap-2 hover:text-[#E91E63] break-all">
                <Mail className="w-4 h-4 shrink-0" /> {RESTAURANT.email}
              </a>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Hours</div>
            <ul className="divide-y divide-[rgba(42,27,61,0.12)]">
              {HOURS.map((h) => (
                <li key={h.day} className="flex justify-between py-2.5">
                  <span className="font-serif text-lg text-[#2A1B3D]">{h.day}</span>
                  <span className="text-sm text-[#4A3F55]">{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <ServiceBadge icon={Utensils} label="Dine-in" testid="service-dine" />
            <ServiceBadge icon={ShoppingBag} label="Takeout" testid="service-takeout" href={RESTAURANT.phoneHref} />
            <ServiceBadge icon={Truck} label="Delivery" testid="service-delivery" href={RESTAURANT.uberEats} external />
          </div>
        </div>

        {/* FORM COLUMN */}
        <div className="lg:col-span-7" data-testid="contact-form-wrap">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-white border border-[rgba(42,27,61,0.1)] p-8 md:p-10"
          >
            <div className="eyebrow mb-3">Send Us a Note</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2A1B3D] tracking-tight mb-8">
              Let&apos;s <span className="italic">talk food</span>.
            </h2>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name" name="name" value={form.name} onChange={onChange} testid="form-name" required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="form-email" required />
            </div>
            <div className="mt-5">
              <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} testid="form-phone" />
            </div>
            <div className="mt-5">
              <label className="block text-xs tracking-[0.22em] uppercase font-bold text-[#4A3F55] mb-2">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                required
                data-testid="form-message"
                className="w-full bg-transparent border-b border-[rgba(42,27,61,0.25)] focus:border-[#2A1B3D] outline-none py-2 text-[#2A1B3D] resize-none"
                placeholder="Tell us how we can help..."
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit"
              className="btn-primary mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send Message"}
              {!loading && <Send className="w-3.5 h-3.5" />}
            </button>
            <p className="mt-4 text-xs text-[#4A3F55]/70">
              We&apos;ll get back to you within 1 business day.
            </p>
          </form>
        </div>
      </section>

      {/* MAP */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 max-w-[1500px] mx-auto" data-testid="contact-map-section">
        <div className="eyebrow mb-5">Map</div>
        <div className="relative w-full h-[420px] border border-[rgba(42,27,61,0.12)] overflow-hidden">
          <iframe
            title="7 Spices location"
            src={RESTAURANT.mapsEmbed}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map-iframe"
          />
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.22em] uppercase font-bold text-[#4A3F55] mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[rgba(42,27,61,0.25)] focus:border-[#2A1B3D] outline-none py-2 text-[#2A1B3D]"
      />
    </div>
  );
}

function ServiceBadge({ icon: Icon, label, testid, href, external }) {
  const className =
    "flex flex-col items-center justify-center text-center p-5 border border-[rgba(42,27,61,0.12)] hover:bg-[#2A1B3D] hover:text-[#FBF8F3] transition-colors group";
  const inner = (
    <>
      <Icon className="w-5 h-5 mb-2 text-[#E91E63] group-hover:text-[#F5C518]" strokeWidth={1.5} />
      <span className="text-xs tracking-[0.22em] uppercase font-bold">{label}</span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        data-testid={testid}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={className}
      >
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testid} className={className}>
      {inner}
    </div>
  );
}
